import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Calendar, Download, TrendingUp, DollarSign, Package, ShoppingCart, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { exportSalesReport } from "../../utils/exportUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSales = () => {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const [summaryRes, dailyRes, topRes, catRes] = await Promise.all([
        axios.get(`${API}/sales/summary`, { headers, params }),
        axios.get(`${API}/sales/daily?days=30`, { headers }),
        axios.get(`${API}/sales/top-products?limit=10`, { headers }),
        axios.get(`${API}/sales/by-category`, { headers }),
      ]);
      
      setSummary(summaryRes.data);
      setDailySales(dailyRes.data);
      setTopProducts(topRes.data);
      setCategorySales(catRes.data);
    } catch (error) {
      toast.error("Failed to fetch sales data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setLoading(true);
    fetchData();
  };

  const handleExport = (format) => {
    try {
      exportSalesReport(dailySales, summary, format);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const kpis = [
    { label: t("totalRevenue"), value: `${summary?.total_revenue?.toFixed(2) || 0} ${t("lyd")}`, icon: DollarSign, color: "bg-green-500" },
    { label: t("totalOrders"), value: summary?.total_orders || 0, icon: ShoppingCart, color: "bg-blue-500" },
    { label: t("profit"), value: `${summary?.profit?.toFixed(2) || 0} ${t("lyd")}`, icon: TrendingUp, color: "bg-[#d4af37]" },
    { label: t("avgOrderValue"), value: `${summary?.average_order_value?.toFixed(2) || 0} ${t("lyd")}`, icon: Package, color: "bg-purple-500" },
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-sales-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-slate-800 animate-pulse rounded-xl" />)}
        </div>
        <div className="h-80 bg-slate-800 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-sales">
      {/* Date Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-slate-300 mb-2 block">{t("startDate")}</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-slate-700 border-slate-600 w-44" />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">{t("endDate")}</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-slate-700 border-slate-600 w-44" />
            </div>
            <Button onClick={handleFilter} className="bg-[#d4af37] text-[#0f172a]">
              <Calendar className="mr-2 h-4 w-4" /> {t("applyFilter")}
            </Button>
            <div className="flex-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-600 text-slate-300">
                  <Download className="mr-2 h-4 w-4" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-800 border-slate-700">
                <DropdownMenuItem onClick={() => handleExport("pdf")} className="text-slate-300 hover:bg-slate-700 cursor-pointer">
                  <FileText className="mr-2 h-4 w-4" /> Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("excel")} className="text-slate-300 hover:bg-slate-700 cursor-pointer">
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${kpi.color}`}>
                  <kpi.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{kpi.value}</p>
                  <p className="text-sm text-slate-400">{kpi.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Sales */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">{t("dailySales")} ({t("last30Days")})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailySales}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px" }} />
                  <Line type="monotone" dataKey="revenue" stroke="#d4af37" strokeWidth={2} dot={{ fill: "#d4af37" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">{t("topSellingProducts")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={100} tickFormatter={(v) => v.slice(0, 15)} />
                  <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px" }} />
                  <Bar dataKey="revenue" fill="#1a365d" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Sales */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white font-heading">{t("salesByCategory")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categorySales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "8px" }} />
                <Bar dataKey="revenue" fill="#d4af37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSales;
